import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    X, ChevronRight, ChevronLeft, SkipForward,
    LayoutDashboard, Dumbbell, BookOpenCheck, ListChecks, Salad, GraduationCap, Trophy,
    Users, Settings, MenuSquare, Sparkles, BarChart3, Crosshair, Filter,
    Compass, PartyPopper, MousePointerClick, Check,
} from "lucide-react";
import { useTutorial } from "../lib/tutorial";
import {
    IllustrationStyles,
    BarbellIllustration,
    StrengthChartIllustration,
    RankChevronIllustration,
    TrophyIllustration,
} from "./TutorialIllustrations";

/**
 * Tab-by-tab integrated tutorial.
 *
 *  • Navigates the user through each main tab while they progress.
 *  • Spotlights specific UI elements via a dim overlay + glowing cutout.
 *  • Floating compact card sits adaptively above/below the spotlight so the
 *    underlying element remains visible.
 *  • Falls back to a clean modal when a step has no target (e.g. welcome).
 *
 * Optional fields per step:
 *   route     — react-router path to navigate to before showing this step
 *   target    — `data-testid` value to spotlight
 *   placement — 'top' | 'bottom' | 'auto' (default: 'auto')
 *   optional  — when true, silently skip the step if the target isn't on the page
 *               (e.g. imbalance card is only present once user has unilateral data)
 *   action    — { label, click? , do? } — adds a "Try it" CTA inside the card that
 *               performs the real interaction (clicks `click` testid, or runs `do(ctx)`).
 *               After firing, the spotlight + card auto-refresh to follow the resulting
 *               UI change, and the user can advance to the next step manually.
 *   illustration — a React element rendered in a desktop-only right column. Used for
 *               concept-heavy steps (Welcome, Strength chart, Rank, Finale) where a
 *               visual element makes the step feel designed rather than informational.
 *               Hidden on viewports < md so mobile cards stay compact.
 */
const STEPS = [
    {
        id: "welcome", icon: Sparkles,
        eyebrow: "Welcome to Hypergains",
        title: "Mechanical tension. No fluff.",
        body: (
            <>
                <p>This walkthrough takes you tab-by-tab through the whole app — about <b className="text-foreground">2 minutes</b>. You'll see exactly where each feature lives.</p>
                <p>Skip anytime, or replay it later from <b>Menu ▾</b> → <b>Show tutorial</b>.</p>
            </>
        ),
        route: "/dashboard",
        illustration: <BarbellIllustration />,
    },
    {
        id: "nav-tabs", icon: Compass,
        eyebrow: "The tab strip",
        title: "Every tab, one tap.",
        body: <p>Your primary navigation. Swipe horizontally on mobile or use the <b className="text-foreground">Menu ▾</b> button on the right to see them all in a vertical list. The active tab is underlined in red.</p>,
        route: "/dashboard", target: "nav-tabs", placement: "bottom",
    },
    {
        id: "nav-more", icon: MenuSquare,
        eyebrow: "Menu ▾",
        title: "Drawer + tutorial replay.",
        body: <p>This dropdown gives you instant access to every tab plus Sign-out — and the <b className="text-foreground">Show tutorial</b> entry that re-launches this guide whenever you want.</p>,
        target: "nav-more", placement: "bottom",
        action: { label: "Open the menu", click: "nav-more" },
    },
    {
        id: "dashboard", icon: LayoutDashboard,
        eyebrow: "Tab 1 · Dashboard",
        title: "Your next session.",
        body: <p>This card shows your next scheduled workout based on your split, last finished session, and local timezone. If you've missed a couple days it auto-rotates to "what comes next" instead of skipping ahead in the calendar. Muscle badges show what you'll train today, and the counter chip tracks how many sessions you've logged this week.</p>,
        route: "/dashboard", target: "next-session-card", placement: "top",
    },
    {
        id: "strength-chart", icon: BarChart3,
        eyebrow: "Tab 1 · Dashboard",
        title: "Strength over time.",
        body: <p>This chart plots your weekly tonnage (weight × reps × sets) over the last 12 weeks. The trend tells you whether you're progressively overloading or stalling.</p>,
        target: "strength-chart", placement: "top", optional: true,
        illustration: <StrengthChartIllustration />,
    },
    {
        id: "rank-card", icon: Trophy,
        eyebrow: "Tab 1 · Dashboard",
        title: "Rank + EXP.",
        body: <p>Every working set earns EXP. 20 ranks across 6 tiers — Foundation to Legend. The "View all ranks" link opens the full ladder.</p>,
        target: "rank-card", placement: "top",
        action: { label: "View the rank ladder", click: "rank-card-view-all" },
        illustration: <RankChevronIllustration />,
    },
    {
        id: "imbalance", icon: Crosshair,
        eyebrow: "Tab 1 · Dashboard",
        title: "Side imbalance watch.",
        body: <p>If you log unilateral exercises (one side at a time), we track left vs right and flag gaps ≥ 10% — folding both weight and reps via Epley e1RM. The Session page also nudges you to start with the lagging side.</p>,
        target: "imbalance-card", placement: "top", optional: true,
    },
    {
        id: "workouts", icon: Dumbbell,
        eyebrow: "Tab 2 · Workouts",
        title: "Pick a day, start lifting.",
        body: <p>Pre-built splits ready to go. Tap a day tab (Upper A / Lower A / etc.) to see the slot order, then hit <b className="text-foreground">Start session</b>. Advanced lifters can drag-reorder slots before starting.</p>,
        route: "/workouts", target: "workouts-page", placement: "bottom",
    },
    {
        id: "start-session", icon: ListChecks,
        eyebrow: "Tab 2 · Workouts",
        title: "Inside a session.",
        body: (
            <>
                <p>Per set you enter <b className="text-foreground">weight, reps, RIR</b> (Reps in Reserve — your safety buffer; 1–2 is the sweet spot).</p>
                <p>Warm-ups are tracked separately. The first lift per muscle gets 2 warm-ups, every other gets 1. Unilateral lifts capture L/R independently for the imbalance watch.</p>
            </>
        ),
        target: "start-session-btn", placement: "top", optional: true,
    },
    {
        id: "exercises", icon: BookOpenCheck,
        eyebrow: "Tab 3 · Exercise Library",
        title: "Every lift, dissected.",
        body: <p>Stable, joint-friendly exercises. Each card has a muscle map showing what it actually trains, looped form demos (swipeable through two YouTube embeds + a fallback gif), and curated setup / execution / mistakes guidance.</p>,
        route: "/exercises", target: "exercises-page", placement: "bottom",
    },
    {
        id: "exercises-filter", icon: Filter,
        eyebrow: "Tab 3 · Exercise Library",
        title: "Filter by muscle or unilateral.",
        body: <p>Use the muscle filters at the top to narrow the library, or tap <b className="text-foreground">Unilateral only</b> when you want a one-arm/one-leg variant for side-imbalance work.</p>,
        target: "filter-unilateral", placement: "bottom", optional: true,
        action: { label: "Try the unilateral filter", click: "filter-unilateral" },
    },
    {
        id: "education", icon: GraduationCap,
        eyebrow: "Tab 4 · Education",
        title: "Read once, know forever.",
        body: <p>Short evidence-based articles covering mechanical tension, rep ranges, double progression, warm-up protocol, junk volume, recovery, protein, fat loss, and unilateral training. Bookmark the ones that change how you train.</p>,
        route: "/education", target: "education-page", placement: "bottom",
    },
    {
        id: "nutrition", icon: Salad,
        eyebrow: "Tab 5 · Nutrition",
        title: "Calories, macros, steps.",
        body: <p>TDEE calculator (Mifflin-St Jeor), macro splits by goal (Gain / Recomp / Fat Loss), and a daily step target that scales with your mode. Protein guidance is laid out in plain numbers.</p>,
        route: "/nutrition", target: "calc-tdee", placement: "top", optional: true,
    },
    {
        id: "ranks", icon: Trophy,
        eyebrow: "Tab 6 · Ranks",
        title: "The ladder.",
        body: <p>All 20 ranks across 6 tiers. Locked taglines stay masked ("????") until you reach them — except the next rank, which gets a sneak peek. Crossing a tier triggers a celebration card you can share to X, Facebook, Messenger, WhatsApp, Instagram, etc.</p>,
        route: "/ranks", placement: "bottom",
    },
    {
        id: "squad", icon: Users,
        eyebrow: "Tab 7 · Squad",
        title: "Private leaderboard.",
        body: <p>Create or join a 5-person training pod. The weekly leaderboard ranks members by EXP earned, tonnage moved, and sessions logged. Share your code via native iOS/Android share sheet.</p>,
        route: "/squad", placement: "bottom",
    },
    {
        id: "account", icon: Settings,
        eyebrow: "Tab 8 · Account",
        title: "Tune the program to your life.",
        body: <p>Set your experience band, current goal (Gain / Recomp / Fat Loss), split (Upper-Lower or Full Body), and — for Upper/Lower — the weekly rhythm that matches how you actually train. The dashboard's Next Session card uses this to know when you're on a rest day.</p>,
        route: "/account", target: "account-page", placement: "bottom",
    },
    {
        id: "finale", icon: PartyPopper,
        eyebrow: "You're set.",
        title: "Now go build something that doesn't move when others push.",
        body: (
            <>
                <p>Hit <b className="text-foreground">Got it</b>, jump back to the dashboard, and start your first session. The rest timer + warm-up protocol will guide you set by set.</p>
                <p>Need to revisit any step later? <b className="text-foreground">Menu ▾ → Show tutorial</b>.</p>
            </>
        ),
        route: "/dashboard",
        illustration: <TrophyIllustration />,
    },
];

const CARD_W = 380;
const CARD_W_WIDE = 520; // when an illustration is present + viewport >= md
const CARD_H_ESTIMATE = 310;
const CARD_H_ESTIMATE_WIDE = 320; // illustration column adds slight height
const MD_BREAKPOINT = 768;
const SPOTLIGHT_PAD = 10;
const VIEWPORT_PAD = 12;
// Emergent preview badge sits at bottom-right (≈170×42 with margin). We add a
// safe zone so the tutorial card's bottom-right never overlaps the Next button.
// On mobile the badge is taller relative to viewport, so we widen the zone there.
const BADGE_SAFE_W = 220;
const BADGE_SAFE_H = 110;

function rectsOverlap(a, b) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
}

function avoidBadge(top, left, cardW, cardH, viewportW, viewportH) {
    const badge = {
        left: viewportW - BADGE_SAFE_W,
        top: viewportH - BADGE_SAFE_H,
        right: viewportW,
        bottom: viewportH,
    };
    const card = { left, top, right: left + cardW, bottom: top + cardH };
    if (!rectsOverlap(card, badge)) return { top, left };
    // Try shifting up first (most subtle move)
    const liftedTop = badge.top - cardH - VIEWPORT_PAD;
    if (liftedTop >= VIEWPORT_PAD) return { top: liftedTop, left };
    // Couldn't lift — slide left instead
    const shiftedLeft = badge.left - cardW - VIEWPORT_PAD;
    if (shiftedLeft >= VIEWPORT_PAD) return { top, left: shiftedLeft };
    // Last resort: dock to top-left under the navbar
    return { top: VIEWPORT_PAD + 60, left: VIEWPORT_PAD };
}

function findTargetEl(testid) {
    return document.querySelector(`[data-testid="${testid}"]`);
}

function waitForTarget(testid, retries = 20, delay = 80) {
    return new Promise((resolve) => {
        let tries = 0;
        const tick = () => {
            const el = findTargetEl(testid);
            if (el) {
                // Instant (not smooth) scroll: smooth animation fires 30+ scroll
                // events that make the card chase the target. Instant snap means
                // a single, stable rect to measure.
                try { el.scrollIntoView({ behavior: "auto", block: "center", inline: "nearest" }); } catch (_) {}
                setTimeout(() => {
                    const r = el.getBoundingClientRect();
                    if (r.width > 0 && r.height > 0) resolve({ rect: r, el });
                    else if (tries++ < retries) setTimeout(tick, delay);
                    else resolve(null);
                }, 120);
                return;
            }
            if (tries++ < retries) setTimeout(tick, delay);
            else resolve(null);
        };
        tick();
    });
}

function placeCard(rect, placement, viewportW, viewportH, opts = {}) {
    const wantsWide = !!opts.wide && viewportW >= MD_BREAKPOINT;
    const baseCardW = wantsWide ? CARD_W_WIDE : CARD_W;
    const cardH = wantsWide ? CARD_H_ESTIMATE_WIDE : CARD_H_ESTIMATE;
    const cardW = Math.min(baseCardW, viewportW - VIEWPORT_PAD * 2);
    if (!rect) {
        const top0 = Math.max(VIEWPORT_PAD, viewportH - cardH - 60);
        const left0 = Math.max(VIEWPORT_PAD, (viewportW - cardW) / 2);
        const { top, left } = avoidBadge(top0, left0, cardW, cardH, viewportW, viewportH);
        return { top, left, placement: "centered", cardW, cardH };
    }
    let chosen = placement === "auto" ? "bottom" : placement;
    if (chosen === "bottom" && rect.bottom + SPOTLIGHT_PAD + cardH > viewportH - VIEWPORT_PAD) {
        if (rect.top - SPOTLIGHT_PAD - cardH >= VIEWPORT_PAD) chosen = "top";
    }
    if (chosen === "top" && rect.top - SPOTLIGHT_PAD - cardH < VIEWPORT_PAD) {
        if (rect.bottom + SPOTLIGHT_PAD + cardH <= viewportH - VIEWPORT_PAD) chosen = "bottom";
    }
    const targetCenterX = rect.left + rect.width / 2;
    let left = targetCenterX - cardW / 2;
    left = Math.max(VIEWPORT_PAD, Math.min(viewportW - cardW - VIEWPORT_PAD, left));
    const top0 = chosen === "top"
        ? Math.max(VIEWPORT_PAD, rect.top - SPOTLIGHT_PAD - cardH)
        : Math.min(viewportH - cardH - VIEWPORT_PAD, rect.bottom + SPOTLIGHT_PAD);
    const { top, left: finalLeft } = avoidBadge(top0, left, cardW, cardH, viewportW, viewportH);
    return { top, left: finalLeft, placement: chosen, cardW, cardH };
}

export default function Tutorial() {
    const { isOpen, closeTutorial } = useTutorial();
    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(0);
    const [targetState, setTargetState] = useState(null);
    const [actionFired, setActionFired] = useState(false);
    const cardRef = useRef(null);
    const stepRef = useRef(step);
    stepRef.current = step;

    const current = STEPS[step] || STEPS[0];
    const Icon = current.icon;
    const isLast = step === STEPS.length - 1;

    const goTo = useCallback((idx) => setStep(Math.max(0, Math.min(STEPS.length - 1, idx))), []);
    const next = useCallback(() => (isLast ? closeTutorial(true) : goTo(step + 1)), [isLast, closeTutorial, goTo, step]);
    const prev = useCallback(() => goTo(step - 1), [goTo, step]);

    useEffect(() => { if (isOpen) setStep(0); }, [isOpen]);

    // Reset the per-step "Try it" CTA when stepping
    useEffect(() => { setActionFired(false); }, [step]);

    const fireAction = useCallback(() => {
        const act = current.action;
        if (!act) return;
        try {
            if (act.click) {
                const el = findTargetEl(act.click);
                if (el) {
                    el.click();
                }
            } else if (typeof act.do === "function") {
                act.do({ navigate });
            }
        } catch (_) { /* never let an action error crash the tutorial */ }
        setActionFired(true);
        // Re-measure the spotlight target after the action may have shifted layout
        setTimeout(() => {
            if (!current.target) return;
            const el = findTargetEl(current.target);
            if (!el) return;
            const r = el.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) return;
            const pos = placeCard(r, current.placement || "auto", window.innerWidth, window.innerHeight, { wide: !!current.illustration });
            setTargetState({ rect: r, position: pos });
        }, 220);
    }, [current, navigate]);

    // Navigate when entering a step that demands a different route
    useEffect(() => {
        if (!isOpen) return;
        if (current.route && location.pathname !== current.route) {
            navigate(current.route);
        }
    }, [isOpen, step, current.route, location.pathname, navigate]);

    // Resolve target rect after route + DOM settle
    useLayoutEffect(() => {
        if (!isOpen) { setTargetState(null); return; }
        let cancelled = false;
        setTargetState(null);

        const resolve = async () => {
            const wide = !!current.illustration;
            if (!current.target) {
                const pos = placeCard(null, "auto", window.innerWidth, window.innerHeight, { wide });
                if (!cancelled) setTargetState({ rect: null, position: pos });
                return;
            }
            const found = await waitForTarget(current.target);
            if (cancelled) return;
            if (!found) {
                if (current.optional) {
                    // Silently skip if optional & not on page
                    if (stepRef.current === step) next();
                    return;
                }
                const pos = placeCard(null, "auto", window.innerWidth, window.innerHeight, { wide });
                setTargetState({ rect: null, position: pos });
                return;
            }
            const pos = placeCard(found.rect, current.placement || "auto", window.innerWidth, window.innerHeight, { wide });
            setTargetState({ rect: found.rect, position: pos });
        };
        resolve();

        const refresh = () => {
            if (!current.target) return;
            const el = findTargetEl(current.target);
            if (!el) return;
            const r = el.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) return;
            // On scroll/resize, ONLY update the spotlight rect — keep the card
            // position stable (otherwise it visibly jitters as it chases the
            // target through every scroll frame). The card was placed once when
            // the step entered; let it stay there until the next step.
            setTargetState((prev) => {
                if (!prev) return prev;
                // Bail if rect change is sub-pixel noise
                if (Math.abs(prev.rect?.top - r.top) < 1 && Math.abs(prev.rect?.left - r.left) < 1
                    && Math.abs(prev.rect?.width - r.width) < 1 && Math.abs(prev.rect?.height - r.height) < 1) {
                    return prev;
                }
                return { ...prev, rect: r };
            });
        };
        // rAF-throttle so we coalesce bursts of scroll events into one update
        let rafId = null;
        const throttled = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => { rafId = null; refresh(); });
        };
        window.addEventListener("resize", throttled);
        window.addEventListener("scroll", throttled, true);
        return () => {
            cancelled = true;
            if (rafId !== null) cancelAnimationFrame(rafId);
            window.removeEventListener("resize", throttled);
            window.removeEventListener("scroll", throttled, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, step]);

    // Keyboard nav
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeTutorial(true);
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, closeTutorial, next, prev]);

    // After the card renders, measure its ACTUAL height (estimates can be wrong
    // when copy wraps to many lines on mobile). If it overflows the badge zone
    // OR the viewport bottom, lift it up so the Next button stays clickable.
    // Runs once per step (not on every scroll-triggered rect change).
    const correctedStepRef = useRef(-1);
    useLayoutEffect(() => {
        if (!targetState) return;
        if (correctedStepRef.current === step) return; // already corrected this step
        const el = cardRef.current;
        if (!el) return;
        const recompute = () => {
            const r = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            const badgeTop = vh - BADGE_SAFE_H;
            const badgeLeft = vw - BADGE_SAFE_W;
            let { top, left } = targetState.position;
            // 1. Hard cap: card must not exceed viewport bottom
            const maxTop = vh - r.height - VIEWPORT_PAD;
            if (top > maxTop) top = Math.max(VIEWPORT_PAD, maxTop);
            // 2. Avoid badge overlap (only matters when card's right edge crosses badgeLeft)
            const cardRight = left + r.width;
            const cardBottom = top + r.height;
            if (cardRight > badgeLeft && cardBottom > badgeTop) {
                const lifted = badgeTop - r.height - VIEWPORT_PAD;
                if (lifted >= VIEWPORT_PAD) {
                    top = lifted;
                } else {
                    const shifted = badgeLeft - r.width - VIEWPORT_PAD;
                    if (shifted >= VIEWPORT_PAD) left = shifted;
                }
            }
            // Only commit if the delta is meaningful (> 2px) to dodge sub-pixel oscillation
            if (Math.abs(top - targetState.position.top) > 2 || Math.abs(left - targetState.position.left) > 2) {
                setTargetState((prev) => prev ? { ...prev, position: { ...prev.position, top, left } } : prev);
            }
            correctedStepRef.current = step;
        };
        const raf = requestAnimationFrame(recompute);
        const t = setTimeout(recompute, 140);
        return () => { cancelAnimationFrame(raf); clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, targetState?.position?.top, targetState?.position?.left]);

    if (!isOpen) return null;
    if (!targetState) {
        return <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-[2px]" data-testid="tutorial-modal" />;
    }

    const { rect, position } = targetState;
    const hasTarget = !!rect;

    return (
        <div className="fixed inset-0 z-[70]" data-testid="tutorial-modal" aria-modal="true" role="dialog">
            {/* Backdrop with cutout via SVG mask */}
            <svg className="absolute inset-0 h-full w-full" style={{ position: "fixed" }} onClick={() => closeTutorial(true)}>
                <defs>
                    <mask id="hg-tutorial-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {hasTarget && (
                            <rect
                                x={Math.max(0, rect.left - SPOTLIGHT_PAD)}
                                y={Math.max(0, rect.top - SPOTLIGHT_PAD)}
                                width={rect.width + SPOTLIGHT_PAD * 2}
                                height={rect.height + SPOTLIGHT_PAD * 2}
                                rx="6" ry="6" fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#hg-tutorial-mask)" />
            </svg>

            {/* Spotlight ring */}
            {hasTarget && (
                <div
                    className="pointer-events-none fixed rounded-md ring-2 ring-accent-red"
                    style={{
                        left: rect.left - SPOTLIGHT_PAD,
                        top: rect.top - SPOTLIGHT_PAD,
                        width: rect.width + SPOTLIGHT_PAD * 2,
                        height: rect.height + SPOTLIGHT_PAD * 2,
                        animation: "hg-tutorial-pulse 1.6s ease-in-out infinite",
                    }}
                    data-testid="tutorial-spotlight"
                />
            )}

            {/* Card */}
            <div
                ref={cardRef}
                onClick={(e) => e.stopPropagation()}
                className="fixed border-2 border-accent-red text-white shadow-2xl backdrop-blur-md backdrop-saturate-150"
                style={{
                    top: position.top,
                    left: position.left,
                    width: position.cardW || Math.min(CARD_W, window.innerWidth - VIEWPORT_PAD * 2),
                    backgroundColor: "rgba(10, 10, 10, 0.62)",
                }}
                data-testid="tutorial-card"
            >
                <IllustrationStyles />
                <button
                    onClick={() => closeTutorial(true)}
                    aria-label="Close tutorial"
                    data-testid="tutorial-close"
                    className="absolute right-2 top-2 z-10 rounded-full p-1 text-white/45 hover:text-white"
                >
                    <X className="h-3.5 w-3.5" />
                </button>

                {/* Step indicators */}
                <div className="border-b border-white/10 px-5 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="label-eyebrow text-accent-red">{current.eyebrow}</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/55" data-testid="tutorial-progress">
                            {step + 1} / {STEPS.length}
                        </div>
                    </div>
                    <div className="mt-2 flex gap-1">
                        {STEPS.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-accent-red" : "bg-white/15"}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Mobile illustration banner — primary surface for our users */}
                {current.illustration && (
                    <div
                        className="md:hidden border-b border-white/10 px-5 pt-4 pb-3 flex justify-center"
                        data-testid="tutorial-illustration-mobile"
                        aria-hidden
                    >
                        <div className="h-[88px] w-[120px] opacity-95">
                            {current.illustration}
                        </div>
                    </div>
                )}

                {/* Body (+ optional desktop right-column illustration) */}
                <div className={current.illustration ? "grid grid-cols-1 md:grid-cols-[1fr_160px] md:gap-4" : ""}>
                    <div className="px-5 py-4 sm:px-6 sm:py-5">
                        <div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent-red/15 text-accent-red">
                            <Icon className="h-5 w-5" />
                        </div>
                        <h2
                            className="font-display text-xl font-black uppercase leading-tight sm:text-2xl"
                            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.95), 0 2px 12px rgba(0,0,0,0.7)" }}
                            data-testid="tutorial-title"
                        >
                            {current.title}
                        </h2>
                        <div
                            className="mt-3 space-y-2.5 text-[13px] leading-relaxed text-white"
                            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9), 0 1px 6px rgba(0,0,0,0.6)" }}
                            data-testid="tutorial-body"
                        >
                            {current.body}
                        </div>
                        {current.action && (
                            <button
                                onClick={fireAction}
                                data-testid="tutorial-try-it"
                                className={`mt-4 inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition ${
                                    actionFired
                                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
                                        : "border-accent-red bg-accent-red/15 text-accent-red hover:bg-accent-red hover:text-white"
                                }`}
                                disabled={actionFired}
                            >
                                {actionFired ? (
                                    <>
                                        <Check className="h-3.5 w-3.5" />
                                        Done — keep going
                                    </>
                                ) : (
                                    <>
                                        <MousePointerClick className="h-3.5 w-3.5" />
                                        {current.action.label}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    {current.illustration && (
                        <div
                            className="hidden md:flex items-center justify-center pr-5 py-4"
                            data-testid="tutorial-illustration"
                            aria-hidden
                        >
                            <div className="h-[160px] w-[160px] opacity-95">
                                {current.illustration}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-white/10 bg-black/40 px-4 py-3">
                    <button
                        onClick={() => closeTutorial(true)}
                        data-testid="tutorial-skip"
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-white/55 hover:text-white"
                    >
                        <SkipForward className="h-3 w-3" /> Skip
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={prev}
                            disabled={step === 0}
                            data-testid="tutorial-prev"
                            className="inline-flex items-center gap-1 rounded-sm border border-white/20 bg-white/5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronLeft className="h-3 w-3" /> Back
                        </button>
                        <button
                            onClick={next}
                            data-testid="tutorial-next"
                            className="inline-flex items-center gap-1 rounded-sm bg-accent-red px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]"
                        >
                            {isLast ? "Got it" : "Next"} <ChevronRight className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes hg-tutorial-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(255,59,48,0.7); }
                    50%      { box-shadow: 0 0 0 10px rgba(255,59,48,0.0); }
                }
            `}</style>
        </div>
    );
}
